import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'components/ui/Modal'
import { Input } from 'components/ui/Input'
import { Textarea } from 'components/ui/Textarea'
import { Button } from 'components/ui/Button'
import { FormField } from 'components/ui/FormField'
import { ModernDatePicker } from 'components/ui/ModernDatePicker'
import { useToast } from 'contexts/ToastContext'
import { type Candidate } from 'lib/api'

type MeetingType = 'call' | 'interview'

interface ScheduleMeetingModalProps {
  isOpen: boolean
  onClose: () => void
  candidate: Candidate
  defaultType: MeetingType
}

export function ScheduleMeetingModal({
  isOpen,
  onClose,
  candidate,
  defaultType,
}: ScheduleMeetingModalProps) {
  const { addToast } = useToast()
  const { t } = useTranslation()

  const defaultTitle = useMemo(() => {
    if (!candidate) return ''
    if (defaultType === 'interview') {
      return t('candidateDetail.scheduleMeetingModal.interviewTitle', {
        name: candidate.name,
      })
    }
    return t('candidateDetail.scheduleMeetingModal.callTitle', {
      name: candidate.name,
    })
  }, [candidate, defaultType, t])

  const [title, setTitle] = useState(defaultTitle)
  const [scheduledFor, setScheduledFor] = useState<Date | null>(null)
  const [duration, setDuration] = useState(30)
  const [location, setLocation] = useState('')
  const [attendeesInput, setAttendeesInput] = useState(candidate?.email || '')
  const [notes, setNotes] = useState('')
  const [errors, setErrors] = useState<{ scheduledFor?: string }>({})

  useEffect(() => {
    if (isOpen) {
      setTitle(defaultTitle)
      setScheduledFor(null)
      setDuration(30)
      setLocation('')
      setAttendeesInput(candidate?.email || '')
      setNotes('')
      setErrors({})
    }
  }, [isOpen, defaultTitle, candidate])

  const formatDateForICS = (date: Date) => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'
  }

  const escapeICSText = (text: string) => {
    return text.replace(/\\/g, '\\\\').replace(/,/g, '\\,').replace(/;/g, '\\;').replace(/\n/g, '\\n')
  }

  const buildGoogleCalendarLink = (start: Date, end: Date, description: string) => {
    const format = (date: Date) => formatDateForICS(date)
    const details = [
      description,
      notes ? `Notes: ${notes}` : '',
      candidate.phone ? `Candidate phone: ${candidate.phone}` : '',
    ]
      .filter(Boolean)
      .join('\n')

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: title,
      dates: `${format(start)}/${format(end)}`,
      details,
    })

    if (location) {
      params.append('location', location)
    }

    if (attendeesInput) {
      const attendees = attendeesInput
        .split(',')
        .map((attendee) => attendee.trim())
        .filter(Boolean)

      attendees.forEach((email) => params.append('add', email))
    }

    return `https://calendar.google.com/calendar/render?${params.toString()}`
  }

  const downloadICSFile = (content: string) => {
    const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${title.replace(/\s+/g, '_')}.ics`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!scheduledFor) {
      setErrors({
        scheduledFor: t(
          'candidateDetail.scheduleMeetingModal.validation.dateRequired'
        ),
      })
      return
    }

    const startDate = new Date(scheduledFor)
    const endDate = new Date(startDate.getTime() + duration * 60000)

    const attendees = attendeesInput
      .split(',')
      .map((attendee) => attendee.trim())
      .filter(Boolean)

    const descriptionLines = [
      notes,
      `Candidate: ${candidate.name} (${candidate.email})`,
      candidate.phone ? `Phone: ${candidate.phone}` : '',
    ].filter(Boolean)

    const icsLines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Selektia//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:REQUEST',
      'BEGIN:VEVENT',
      `UID:${Date.now()}@selektia`,
      `DTSTAMP:${formatDateForICS(new Date())}`,
      `DTSTART:${formatDateForICS(startDate)}`,
      `DTEND:${formatDateForICS(endDate)}`,
      `SUMMARY:${escapeICSText(title)}`,
      `DESCRIPTION:${escapeICSText(descriptionLines.join('\n'))}`,
    ]

    if (location) {
      icsLines.push(`LOCATION:${escapeICSText(location)}`)
    }

    attendees.forEach((email) => {
      icsLines.push(`ATTENDEE;CN=${escapeICSText(email)};ROLE=REQ-PARTICIPANT:mailto:${email}`)
    })

    icsLines.push('END:VEVENT', 'END:VCALENDAR')

    downloadICSFile(icsLines.join('\r\n'))

    const googleLink = buildGoogleCalendarLink(
      startDate,
      endDate,
      descriptionLines.join('\n')
    )
    window.open(googleLink, '_blank', 'noopener,noreferrer')

    addToast({
      type: 'success',
      title: t('candidateDetail.scheduleMeetingModal.successTitle'),
      message: t('candidateDetail.scheduleMeetingModal.successMessage'),
      duration: 5000,
    })

    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('candidateDetail.scheduleMeetingModal.title')}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid gap-4">
          <FormField
            label={t('candidateDetail.scheduleMeetingModal.meetingTitle')}
            required
          >
            <Input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              required
            />
          </FormField>

          <FormField
            label={t('candidateDetail.scheduleMeetingModal.dateTime')}
            required
            error={errors.scheduledFor}
          >
            <ModernDatePicker
              selected={scheduledFor}
              onChange={(date) => {
                setScheduledFor(date)
                setErrors({})
              }}
              placeholder="Choose a date"
              showTimeSelect
              minDate={new Date()}
            />
          </FormField>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              label={t('candidateDetail.scheduleMeetingModal.duration')}
              required
            >
              <Input
                type="number"
                min={10}
                step={5}
                value={duration}
                onChange={(event) => setDuration(Number(event.target.value))}
                required
              />
            </FormField>

            <FormField
              label={t('candidateDetail.scheduleMeetingModal.location')}
            >
              <Input
                value={location}
                onChange={(event) => setLocation(event.target.value)}
                placeholder={t(
                  'candidateDetail.scheduleMeetingModal.locationPlaceholder'
                )}
              />
            </FormField>
          </div>

          <FormField
            label={t('candidateDetail.scheduleMeetingModal.attendees')}
            description={t(
              'candidateDetail.scheduleMeetingModal.attendeesHelper'
            )}
          >
            <Input
              value={attendeesInput}
              onChange={(event) => setAttendeesInput(event.target.value)}
              placeholder={t(
                'candidateDetail.scheduleMeetingModal.attendeesPlaceholder'
              )}
            />
          </FormField>

          <FormField label={t('candidateDetail.scheduleMeetingModal.notes')}>
            <Textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder={t(
                'candidateDetail.scheduleMeetingModal.notesPlaceholder'
              )}
            />
          </FormField>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('candidateDetail.scheduleMeetingModal.cancel')}
          </Button>
          <div className="flex flex-1 sm:flex-initial flex-col sm:flex-row gap-3 sm:justify-end">
            <Button type="submit" className="w-full sm:w-auto">
              {t('candidateDetail.scheduleMeetingModal.submit')}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  )
}

